/**
 * Password Gate Styles
 */

import styled, { keyframes } from 'styled-components';
import { colors } from '@config/colors';
import { typography } from '@config/typography';
import { spacing, borderRadius } from '@config/spacing';

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
  20%, 40%, 60%, 80% { transform: translateX(4px); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.96); }
  to { opacity: 1; transform: scale(1); }
`;

export const GateContainer = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, ${colors.highlight.extraDark} 0%, ${colors.highlight.dark} 100%);
  z-index: 9999;
`;

export const GateCard = styled.div<{ $hasError?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.xl};
  padding: ${spacing['3xl']} ${spacing['2xl']};
  max-width: 400px;
  width: 90%;
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: ${borderRadius['2xl']};
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.3);
  animation: ${props => props.$hasError ? shake : fadeIn} ${props => props.$hasError ? '0.5s' : '0.6s'} ease;
`;

export const GateLogo = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${colors.brand.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.highlight.dark};
  font-size: ${typography.fontSize.xl};
  font-weight: ${typography.fontWeight.semibold};
  font-family: ${typography.fontFamily.heading};
`;

export const GateTitle = styled.h1`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize.xl};
  font-weight: ${typography.fontWeight.semibold};
  color: white;
  margin: 0;
  text-align: center;
`;

export const GateSubtitle = styled.p`
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.sm};
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
  text-align: center;
`;

export const GateForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
  width: 100%;
`;

export const GateInput = styled.input`
  width: 100%;
  padding: ${spacing.md} ${spacing.lg};
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.base};
  color: white;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: ${borderRadius.lg};
  outline: none;
  transition: border-color 0.2s ease, background 0.2s ease;
  box-sizing: border-box;

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }

  &:focus {
    border-color: ${colors.brand.primary};
    background: rgba(255, 255, 255, 0.1);
  }
`;

export const GateButton = styled.button`
  width: 100%;
  padding: ${spacing.md} ${spacing.lg};
  font-family: ${typography.fontFamily.CTA};
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.medium};
  color: ${colors.highlight.dark};
  background: ${colors.brand.primary};
  border: none;
  border-radius: ${borderRadius.lg};
  cursor: pointer;
  transition: background 0.2s ease, transform 0.1s ease;

  &:hover {
    background: ${colors.brand.primaryDark};
  }

  &:active {
    transform: scale(0.98);
  }
`;

export const ErrorMessage = styled.p`
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.sm};
  color: ${colors.semantic.error};
  margin: 0;
  text-align: center;
`;
